import { useMemo } from "react";
import type { AxiosInstance } from "axios";
import { useMutation, useQueries, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useAxios } from "@/contexts/AxiosContext";
import type { MediaEntityInfo, MediaEntityResponse, UploadImageRequest, UploadPresignedUrlResponse, UploadVideoRequest } from "@/types/api";
import type { ProjectDetailsCharacter, ProjectDetailsLocation, ProjectDetailsStoryboardShot } from "@/types/project";
import {
  MAX_USER_VIDEO_UPLOAD_BYTES,
  USER_VIDEO_UPLOAD_EXTENSION_TO_MIME,
  USER_VIDEO_UPLOAD_FILE_EXTENSIONS
} from "@/utils/constants";
import { librarySortEntityCacheKey, librarySortEntityRef, type LibraryItemSortKeySource } from "@/utils/librarySort";
import { parseCreatedDate } from "@/utils/videoExtend";
import { fetchMediaBatch, mediaBatchRefKey, type MediaBatchItemRef } from "@/services/mediaBatch";
import { getErrorMessage, throwLockAwareError } from "@/api/apiClient";

const requestImageUploadUrl = async (
  axiosClient: AxiosInstance,
  projectId: string,
  body: UploadImageRequest
): Promise<UploadPresignedUrlResponse<"image">> => {
  try {
    const response = await axiosClient.post<UploadPresignedUrlResponse<"image">>(`/uploads/${encodeURIComponent(projectId)}/image`, body);
    return response.data;
  } catch (error) {
    return throwLockAwareError(error, "Something went wrong when requesting an image upload URL.");
  }
};

const requestVideoUploadUrl = async (
  axiosClient: AxiosInstance,
  projectId: string,
  body: UploadVideoRequest
): Promise<UploadPresignedUrlResponse<"video">> => {
  try {
    const response = await axiosClient.post<UploadPresignedUrlResponse<"video">>(`/uploads/${encodeURIComponent(projectId)}/video`, body);
    return response.data;
  } catch (error) {
    return throwLockAwareError(error, "Something went wrong when requesting a video upload URL.");
  }
};

/**
 * MIME type for POST /uploads/.../image and the subsequent S3 PUT (must match exactly).
 * API accepts e.g. image/jpeg, image/png; some browsers report image/jpg or empty type on drag-drop.
 */
const normalizeImageContentTypeForUpload = (file: File): string => {
  const raw = (file.type ?? "").trim().toLowerCase();
  if (raw === "image/jpg" || raw === "image/pjpeg") {
    return "image/jpeg";
  }

  if (raw.startsWith("image/") && raw.length > "image/".length) {
    return raw;
  }

  const name = file.name.toLowerCase();
  if (name.endsWith(".png")) {
    return "image/png";
  }

  if (name.endsWith(".webp")) {
    return "image/webp";
  }

  if (name.endsWith(".gif")) {
    return "image/gif";
  }

  if (name.endsWith(".bmp")) {
    return "image/bmp";
  }

  if (name.endsWith(".svg") || name.endsWith(".svgz")) {
    return "image/svg+xml";
  }

  if (name.endsWith(".jpg") || name.endsWith(".jpeg")) {
    return "image/jpeg";
  }

  return "image/jpeg";
};

const safeImageUploadFilename = (file: File): string => {
  const n = file.name?.trim();
  return n && n.length > 0 ? n : "image.jpg";
};

const safeVideoUploadFilename = (file: File): string => {
  const n = file.name?.trim();
  return n && n.length > 0 ? n : "video.mp4";
};

/**
 * MIME type for POST /uploads/.../video and the subsequent S3 PUT (must match exactly).
 * The API only accepts video/mp4.
 */
const normalizeVideoContentTypeForUpload = (file: File): string => {
  const raw = (file.type ?? "").trim().toLowerCase();
  if (raw === "video/mp4") {
    return "video/mp4";
  }

  const name = file.name.toLowerCase();
  for (const ext of USER_VIDEO_UPLOAD_FILE_EXTENSIONS) {
    if (name.endsWith(ext)) {
      return USER_VIDEO_UPLOAD_EXTENSION_TO_MIME[ext];
    }
  }

  throw new Error("Only MP4 files (video/mp4) are supported for video upload.");
};

const assertUserVideoUploadFileSize = (file: File): void => {
  if (file.size > MAX_USER_VIDEO_UPLOAD_BYTES) {
    const mb = file.size / (1024 * 1024);
    throw new Error(`Video must be under 20 MB (this file is about ${mb.toFixed(1)} MB).`);
  }
};

/**
 * Request presigned URL, upload file to S3, return the new image id.
 * Use useUploadImage() in components.
 */
const uploadImageFile = async (
  axiosClient: AxiosInstance,
  projectId: string,
  file: File,
  options?: { readonly source?: string; readonly parentImageIds?: readonly string[] }
): Promise<{ imageId: string }> => {
  const contentType = normalizeImageContentTypeForUpload(file);
  const { url, id } = await requestImageUploadUrl(axiosClient, projectId, {
    filename: safeImageUploadFilename(file),
    entity: "image",
    content_type: contentType,
    ...(options?.source ? { source: options.source } : {}),
    ...(options?.parentImageIds && options.parentImageIds.length > 0 ? { parent_image_ids: options.parentImageIds } : {})
  });
  // Use fetch for S3 presigned URL: our axios client adds Authorization header,
  // which was not part of the signature and causes S3 to return 400.
  const putRes = await fetch(url, {
    method: "PUT",
    headers: { "Content-Type": contentType },
    body: file
  });
  if (!putRes.ok) {
    throw new Error(`Upload failed (${putRes.status}).`);
  }

  return { imageId: id };
};

function putVideoFileToPresignedUrlWithProgress(url: string, file: File, contentType: string, onProgress: (percent: number) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    xhr.setRequestHeader("Content-Type", contentType);
    xhr.upload.addEventListener("progress", (ev) => {
      if (ev.lengthComputable) {
        onProgress(Math.round((ev.loaded / ev.total) * 100));
      }
    });
    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress(100);
        resolve();
      } else {
        reject(new Error(`Upload failed (${xhr.status}).`));
      }
    });
    xhr.addEventListener("error", () => reject(new Error("Network error during upload.")));
    xhr.send(file);
  });
}

/** Request presigned URL, upload file to S3, return the new video id. Use useUploadVideo() in components. */
const uploadVideoFile = async (axiosClient: AxiosInstance, projectId: string, file: File): Promise<{ videoId: string }> => {
  assertUserVideoUploadFileSize(file);
  const contentType = normalizeVideoContentTypeForUpload(file);
  const { url, id } = await requestVideoUploadUrl(axiosClient, projectId, {
    filename: safeVideoUploadFilename(file),
    entity: "video",
    content_type: contentType
  });
  const putRes = await fetch(url, {
    method: "PUT",
    headers: { "Content-Type": contentType },
    body: file
  });
  if (!putRes.ok) {
    throw new Error(`Upload failed (${putRes.status}).`);
  }

  return { videoId: id };
};

/** Rejects video files over 20 MiB (same limit as presigned upload helpers). */
export const assertVideoUploadUnderSizeLimit = (file: File): void => {
  assertUserVideoUploadFileSize(file);
};

/** Presigned video upload with XHR progress callbacks (0–100) for the S3 PUT. */
export const uploadVideoFileWithProgress = async (
  axiosClient: AxiosInstance,
  projectId: string,
  file: File,
  onProgress: (percent: number) => void
): Promise<{ videoId: string }> => {
  assertUserVideoUploadFileSize(file);
  const contentType = normalizeVideoContentTypeForUpload(file);
  const { url, id } = await requestVideoUploadUrl(axiosClient, projectId, {
    filename: safeVideoUploadFilename(file),
    entity: "video",
    content_type: contentType
  });
  await putVideoFileToPresignedUrlWithProgress(url, file, contentType, onProgress);
  return { videoId: id };
};

/** Response from GET /{projectId}/image or /{projectId}/video (list project media ids, newest first). */
interface ProjectMediaIdsResponse {
  readonly ids?: readonly string[] | ReadonlyArray<{ id: string }>;
  readonly last_modified?: Readonly<Record<string, number>>;
}

export interface ProjectMediaList {
  readonly ids: readonly string[];
  /** id -> S3 LastModified epoch ms; empty when the API predates the field. */
  readonly lastModifiedById: Readonly<Record<string, number>>;
}

const fetchProjectMediaList = async (axiosClient: AxiosInstance, projectId: string, kind: "image" | "video"): Promise<ProjectMediaList> => {
  try {
    const { data } = await axiosClient.get<ProjectMediaIdsResponse>(`/${encodeURIComponent(projectId)}/${kind}`);
    const raw = Array.isArray(data?.ids) ? data.ids : [];
    const ids = raw
      .map((item): string | null => (typeof item === "string" ? item : ((item as { id?: string })?.id ?? null)))
      .filter((id): id is string => typeof id === "string");
    return { ids, lastModifiedById: data?.last_modified ?? {} };
  } catch (error) {
    throw new Error(getErrorMessage(error, `Something went wrong when listing project ${kind}s.`));
  }
};

const selectMediaListIds = (list: ProjectMediaList): readonly string[] => list.ids;
const EMPTY_MEDIA_LIST: ProjectMediaList = { ids: [], lastModifiedById: {} };

export type MediaEntityKind = "image" | "video" | "book";

const MEDIA_ENTITY_KIND_LABEL: Record<MediaEntityKind, string> = {
  image: "image",
  video: "video",
  book: "book"
};

/** GET /{projectId}/image/{id}, /{projectId}/video/{id}, or /{projectId}/book/{id} — same response shape. */
export const fetchMediaEntity = async (
  axiosClient: AxiosInstance,
  projectId: string,
  assetId: string,
  kind: MediaEntityKind
): Promise<{ url: string; displayName: string; info?: MediaEntityInfo; thumbnailUrl?: string }> => {
  try {
    const { data } = await axiosClient.get<MediaEntityResponse>(`/${encodeURIComponent(projectId)}/${kind}/${encodeURIComponent(assetId)}`);
    const thumbnailUrl = typeof data.thumbnail_url === "string" ? data.thumbnail_url.trim() : "";
    return {
      url: data.url,
      displayName: data.info?.display_name ?? assetId,
      ...(data.info === undefined ? {} : { info: data.info }),
      ...(thumbnailUrl === "" ? {} : { thumbnailUrl })
    };
  } catch (error) {
    throw new Error(getErrorMessage(error, `Something went wrong when loading the ${MEDIA_ENTITY_KIND_LABEL[kind]}.`));
  }
};

const ONE_MINUTE_MS = 60 * 1000;

/** Well inside the backend's PRE_SIGNED_URL_EXPIRY (2h), so a cached url cannot be an expired one. */
const MEDIA_ENTITY_STALE_MS = 30 * ONE_MINUTE_MS;
const MEDIA_ENTITY_GC_MS = 60 * ONE_MINUTE_MS;

export interface ProjectMediaListQueryOptions {
  /** When false, skips the query (e.g. until the library View All chip is active). */
  readonly enabled?: boolean;
  /** Skips this hook's own batch metadata fetch; ids still return, with displayName falling back to id. */
  readonly skipMetadataFetch?: boolean;
}

export const useProjectImageIds = (projectId: string | null, options?: ProjectMediaListQueryOptions) => {
  const axiosClient = useAxios();
  const enabled = (options?.enabled ?? true) && typeof projectId === "string" && projectId.length > 0;
  return useQuery({
    queryKey: ["projectImageIds", projectId],
    queryFn: async () => fetchProjectMediaList(axiosClient, projectId!, "image"),
    select: selectMediaListIds,
    enabled,
    retry: 1,
    refetchOnWindowFocus: false
  });
};

export interface ProjectImageListItem {
  readonly id: string;
  readonly displayName: string;
  readonly lastModifiedMs?: number;
}

export interface ProjectVideoListItem {
  readonly id: string;
  readonly displayName: string;
  readonly lastModifiedMs?: number;
  /** Optional first reference image id from video entity `info` when the API includes it (e.g. `parent_ids`). */
  readonly posterImageId: string | null;
  /** `VideoInfo.poster_image_id` — frame extracted from the clip on save. */
  readonly extractedPosterImageId: string | null;
}

/** Parent / reference image ids from entity `info` (snake_case or camelCase). */
export const mediaEntityParentIds = (info: MediaEntityInfo | undefined): readonly string[] => {
  const ids = info?.parent_ids ?? info?.parentIds;
  if (ids === undefined || ids.length === 0) {
    return [];
  }

  return ids.filter((id): id is string => typeof id === "string" && id.trim() !== "");
};

const firstVideoPosterImageId = (info: MediaEntityInfo | undefined): string | null => {
  const first = mediaEntityParentIds(info)[0];
  return first ?? null;
};

const extractedPosterImageId = (info: MediaEntityInfo | undefined): string | null => {
  const id = info?.poster_image_id ?? info?.posterImageId;
  const trimmed = typeof id === "string" ? id.trim() : "";
  return trimmed !== "" ? trimmed : null;
};

/**
 * Fetches presigned URL + info for many media refs in one batched call instead of one GET per id
 * (`useQueries`), while still seeding the same per-id cache keys (`[kind, projectId, id]`) that
 * {@link useMediaEntity}/{@link useImageEntity}/{@link useVideoEntity} and Timeline/MainSpace read,
 * so those call sites keep getting instant cache hits unchanged.
 */
const useMediaEntitiesBatch = (projectId: string | null, refs: readonly MediaBatchItemRef[], enabled: boolean) => {
  const axiosClient = useAxios();
  const queryClient = useQueryClient();
  const refsKey = useMemo(() => refs.map((ref) => mediaBatchRefKey(ref)).toSorted().join(","), [refs]);
  const queryEnabled = enabled && typeof projectId === "string" && projectId.length > 0 && refs.length > 0;

  return useQuery({
    queryKey: ["mediaBatch", projectId, refsKey] as const,
    queryFn: async () => {
      const byRef = await fetchMediaBatch(axiosClient, projectId!, refs);
      for (const [key, data] of byRef) {
        const [kind, id] = key.split(":") as [MediaEntityKind, string];
        queryClient.setQueryData([kind, projectId, id], data);
      }

      return byRef;
    },
    enabled: queryEnabled,
    staleTime: MEDIA_ENTITY_STALE_MS,
    gcTime: MEDIA_ENTITY_GC_MS,
    retry: 1,
    refetchOnWindowFocus: false
  });
};

/** All project images in S3 (`GET /{projectId}/image`) with display names from entity metadata. */
export const useProjectImages = (projectId: string | null, options?: ProjectMediaListQueryOptions) => {
  const axiosClient = useAxios();
  const listEnabled = (options?.enabled ?? true) && typeof projectId === "string" && projectId.length > 0;
  const {
    data: list = EMPTY_MEDIA_LIST,
    isLoading: idsLoading,
    isError: idsError
  } = useQuery({
    queryKey: ["projectImageIds", projectId],
    queryFn: async () => fetchProjectMediaList(axiosClient, projectId!, "image"),
    enabled: listEnabled,
    retry: 1,
    refetchOnWindowFocus: false
  });
  const { ids, lastModifiedById } = list;

  const skipMetadataFetch = options?.skipMetadataFetch ?? false;
  const imageRefs = useMemo<MediaBatchItemRef[]>(
    () => (skipMetadataFetch ? [] : ids.map((id) => ({ kind: "image" as const, id }))),
    [ids, skipMetadataFetch]
  );
  const {
    data: byRef,
    isPending: detailsPending,
    isError: detailsError
  } = useMediaEntitiesBatch(projectId, imageRefs, listEnabled);

  const images: ProjectImageListItem[] = useMemo(
    () =>
      ids.map((id) => {
        const data = byRef?.get(mediaBatchRefKey({ kind: "image", id }));
        return {
          id,
          displayName: data?.displayName ?? id,
          lastModifiedMs: lastModifiedById[id]
        };
      }),
    [ids, byRef, lastModifiedById]
  );

  return {
    images,
    isLoading: idsLoading || (imageRefs.length > 0 && detailsPending),
    isError: idsError || (imageRefs.length > 0 && detailsError)
  };
};

const useProjectVideoList = (projectId: string | null, options?: ProjectMediaListQueryOptions) => {
  const axiosClient = useAxios();
  const enabled = (options?.enabled ?? true) && typeof projectId === "string" && projectId.length > 0;
  return useQuery({
    queryKey: ["projectVideoIds", projectId],
    queryFn: async () => fetchProjectMediaList(axiosClient, projectId!, "video"),
    enabled,
    retry: 1,
    refetchOnWindowFocus: false
  });
};

export const useProjectVideoIds = (projectId: string | null, options?: ProjectMediaListQueryOptions) => {
  const axiosClient = useAxios();
  const enabled = (options?.enabled ?? true) && typeof projectId === "string" && projectId.length > 0;
  return useQuery({
    queryKey: ["projectVideoIds", projectId],
    queryFn: async () => fetchProjectMediaList(axiosClient, projectId!, "video"),
    select: selectMediaListIds,
    enabled,
    retry: 1,
    refetchOnWindowFocus: false
  });
};

export const useProjectVideos = (projectId: string | null, options?: ProjectMediaListQueryOptions) => {
  const listEnabled = (options?.enabled ?? true) && typeof projectId === "string" && projectId.length > 0;
  const {
    data: list = EMPTY_MEDIA_LIST,
    isLoading: idsLoading,
    isError: idsError
  } = useProjectVideoList(projectId, options);
  const { ids, lastModifiedById } = list;

  const skipMetadataFetch = options?.skipMetadataFetch ?? false;
  const videoRefs = useMemo<MediaBatchItemRef[]>(
    () => (skipMetadataFetch ? [] : ids.map((id) => ({ kind: "video" as const, id }))),
    [ids, skipMetadataFetch]
  );
  const {
    data: byRef,
    isPending: detailsPending,
    isError: detailsError
  } = useMediaEntitiesBatch(projectId, videoRefs, listEnabled);

  const videos: ProjectVideoListItem[] = useMemo(
    () =>
      ids.map((id) => {
        const data = byRef?.get(mediaBatchRefKey({ kind: "video", id }));
        return {
          id,
          displayName: data?.displayName ?? id,
          lastModifiedMs: lastModifiedById[id],
          posterImageId: firstVideoPosterImageId(data?.info),
          extractedPosterImageId: extractedPosterImageId(data?.info)
        };
      }),
    [ids, byRef, lastModifiedById]
  );

  return {
    videos,
    isLoading: idsLoading || (videoRefs.length > 0 && detailsPending),
    isError: idsError || (videoRefs.length > 0 && detailsError)
  };
};

export const useRequestImageUploadUrl = () => {
  const axiosClient = useAxios();
  return useMutation({
    async mutationFn(vars: { readonly projectId: string; readonly body: UploadImageRequest }) {
      return requestImageUploadUrl(axiosClient, vars.projectId, vars.body);
    }
  });
};

export const useUploadImage = () => {
  const axiosClient = useAxios();
  const queryClient = useQueryClient();
  return useMutation({
    async mutationFn(vars: {
      readonly projectId: string;
      readonly file: File;
      readonly source?: string;
      readonly parentImageIds?: readonly string[];
    }) {
      return uploadImageFile(axiosClient, vars.projectId, vars.file, { source: vars.source, parentImageIds: vars.parentImageIds });
    },
    onSuccess(_data, vars) {
      void queryClient.invalidateQueries({ queryKey: ["projectImageIds", vars.projectId] });
    }
  });
};

export const useUploadVideo = () => {
  const axiosClient = useAxios();
  const queryClient = useQueryClient();
  return useMutation({
    async mutationFn(vars: { readonly projectId: string; readonly file: File }) {
      return uploadVideoFile(axiosClient, vars.projectId, vars.file);
    },
    onSuccess(_data, vars) {
      void queryClient.invalidateQueries({ queryKey: ["projectVideoIds", vars.projectId] });
    }
  });
};

export const useMediaEntity = (projectId: string | null, assetId: string | null, kind: MediaEntityKind) => {
  const axiosClient = useAxios();
  return useQuery({
    queryKey: [kind, projectId, assetId],
    queryFn: async () => fetchMediaEntity(axiosClient, projectId!, assetId!, kind),
    enabled: typeof projectId === "string" && projectId.length > 0 && typeof assetId === "string" && assetId.length > 0,
    retry: 1,
    refetchOnWindowFocus: false,
    staleTime: MEDIA_ENTITY_STALE_MS,
    gcTime: MEDIA_ENTITY_GC_MS
  });
};

export const useImageEntity = (projectId: string | null, imageId: string | null) => useMediaEntity(projectId, imageId, "image");

export const useVideoEntity = (projectId: string | null, videoId: string | null) => useMediaEntity(projectId, videoId, "video");

/**
 * Resolves presigned URLs for an explicit list of video ids in one batched request (rather than one
 * GET per clip) and seeds the same `["video", projectId, id]` cache {@link useVideoEntity} reads, so
 * a later `VideoPlayer` mount for any of these ids is an instant cache hit. Built for "Run Timeline":
 * warm every clip's URL up front instead of resolving it lazily as playback reaches each one.
 */
export const useVideoEntitiesByIds = (projectId: string | null, videoIds: readonly string[]) => {
  const uniqueVideoIds = useMemo(() => [...new Set(videoIds)], [videoIds]);
  const enabled = typeof projectId === "string" && projectId.length > 0 && uniqueVideoIds.length > 0;
  const refs = useMemo<MediaBatchItemRef[]>(() => uniqueVideoIds.map((id) => ({ kind: "video" as const, id })), [uniqueVideoIds]);
  const { data: byRef, isPending, isError } = useMediaEntitiesBatch(projectId, refs, enabled);

  const urlByVideoId = useMemo(() => {
    const map = new Map<string, string>();
    for (const id of uniqueVideoIds) {
      const url = byRef?.get(mediaBatchRefKey({ kind: "video", id }))?.url;
      if (typeof url === "string" && url.length > 0) {
        map.set(id, url);
      }
    }

    return map;
  }, [uniqueVideoIds, byRef]);

  return { urlByVideoId, isPending: enabled && isPending, isError: enabled && isError };
};

/**
 * Resolves presigned URLs for a mixed list of image/video refs in one batched request, seeding the
 * same per-id caches {@link useImageEntity}/{@link useVideoEntity} read. Built for "Run Timeline"'s
 * continuous-playback stage, which needs both video clips and still-image segments warmed up front.
 * Results are keyed by {@link mediaBatchRefKey} (`"image:<id>"` / `"video:<id>"`) since ids aren't
 * unique across kinds.
 */
export const useMediaUrlsByRefs = (projectId: string | null, refs: readonly MediaBatchItemRef[]) => {
  const uniqueRefs = useMemo(() => {
    const seen = new Set<string>();
    const out: MediaBatchItemRef[] = [];
    for (const ref of refs) {
      const key = mediaBatchRefKey(ref);
      if (seen.has(key)) {
        continue;
      }

      seen.add(key);
      out.push(ref);
    }

    return out;
  }, [refs]);

  const enabled = typeof projectId === "string" && projectId.length > 0 && uniqueRefs.length > 0;
  const { data: byRef, isPending, isError } = useMediaEntitiesBatch(projectId, uniqueRefs, enabled);

  const urlByRefKey = useMemo(() => {
    const map = new Map<string, string>();
    for (const ref of uniqueRefs) {
      const key = mediaBatchRefKey(ref);
      const url = byRef?.get(key)?.url;
      if (typeof url === "string" && url.length > 0) {
        map.set(key, url);
      }
    }

    return map;
  }, [uniqueRefs, byRef]);

  return { urlByRefKey, isPending: enabled && isPending, isError: enabled && isError };
};

/**
 * Refetch a dropped asset's presigned urls. Invalidates rather than removes: dropping the cached
 * entry blanks the library card and makes `useLibraryItemsSortedByCreatedDate` report a pending
 * sort, so the row skeletons and jumps to the unsorted position until the refetch lands.
 */
export function refreshMediaEntityQueries(queryClient: QueryClient, projectId: string, imageId: string, videoId?: string | null): void {
  void queryClient.invalidateQueries({ queryKey: ["image", projectId, imageId] });
  const trimmedVideoId = typeof videoId === "string" ? videoId.trim() : "";
  if (trimmedVideoId.length > 0) {
    void queryClient.invalidateQueries({ queryKey: ["video", projectId, trimmedVideoId] });
  }
}

/** Full body of one breakdown-history snapshot (GET /{projectId}/breakdown-history/{id}), read-only. */
export interface BreakdownHistorySnapshot {
  readonly id: string;
  readonly createdDate: string | null;
  readonly characters: readonly ProjectDetailsCharacter[];
  readonly locations: readonly ProjectDetailsLocation[];
  readonly "storyboard-shots": readonly ProjectDetailsStoryboardShot[];
}

interface BreakdownHistoryIdsResponse {
  readonly ids?: readonly string[];
}

const fetchBreakdownHistoryIds = async (axiosClient: AxiosInstance, projectId: string): Promise<readonly string[]> => {
  try {
    const response = await axiosClient.get<BreakdownHistoryIdsResponse>(`/${encodeURIComponent(projectId)}/breakdown-history`);
    const raw = response.data?.ids;
    return Array.isArray(raw) ? raw.filter((id): id is string => typeof id === "string") : [];
  } catch (error) {
    throw new Error(getErrorMessage(error, "Something went wrong when listing breakdown history."));
  }
};

/** GET /{projectId}/breakdown-history — ids only, newest first; fetch a body with useBreakdownHistorySnapshots. */
export const useBreakdownHistoryIds = (projectId: string | null, options?: ProjectMediaListQueryOptions) => {
  const axiosClient = useAxios();
  const enabled = (options?.enabled ?? true) && typeof projectId === "string" && projectId.length > 0;
  return useQuery({
    queryKey: ["breakdownHistoryIds", projectId],
    queryFn: async () => fetchBreakdownHistoryIds(axiosClient, projectId!),
    enabled,
    retry: 1,
    refetchOnWindowFocus: false
  });
};

interface BreakdownHistorySnapshotResponse {
  readonly snapshot?: {
    readonly id: string;
    readonly "created-date"?: string;
    readonly characters?: readonly ProjectDetailsCharacter[];
    readonly locations?: readonly ProjectDetailsLocation[];
    readonly "storyboard-shots"?: readonly ProjectDetailsStoryboardShot[];
  };
}

const fetchBreakdownHistorySnapshot = async (
  axiosClient: AxiosInstance,
  projectId: string,
  snapshotId: string
): Promise<BreakdownHistorySnapshot | null> => {
  try {
    const {
      data: { snapshot }
    } = await axiosClient.get<BreakdownHistorySnapshotResponse>(
      `/${encodeURIComponent(projectId)}/breakdown-history/${encodeURIComponent(snapshotId)}`
    );
    if (!snapshot) {
      return null;
    }

    return {
      id: snapshot.id,
      createdDate: snapshot["created-date"] ?? null,
      characters: snapshot.characters ?? [],
      locations: snapshot.locations ?? [],
      "storyboard-shots": snapshot["storyboard-shots"] ?? []
    };
  } catch (error) {
    throw new Error(getErrorMessage(error, "Something went wrong when loading a previous breakdown."));
  }
};

/** Full bodies for a (small, capped at 5) list of breakdown-history ids, fetched individually in parallel. */
export const useBreakdownHistorySnapshots = (
  projectId: string | null,
  snapshotIds: readonly string[],
  enabled: boolean
): { readonly snapshots: readonly BreakdownHistorySnapshot[]; readonly isLoading: boolean } => {
  const axiosClient = useAxios();
  const queryEnabled = enabled && typeof projectId === "string" && projectId.length > 0;

  const results = useQueries({
    queries: snapshotIds.map((snapshotId) => ({
      queryKey: ["breakdownHistorySnapshot", projectId, snapshotId] as const,
      queryFn: async () => fetchBreakdownHistorySnapshot(axiosClient, projectId!, snapshotId),
      enabled: queryEnabled,
      staleTime: MEDIA_ENTITY_STALE_MS,
      gcTime: MEDIA_ENTITY_GC_MS,
      retry: 1,
      refetchOnWindowFocus: false
    }))
  });

  return useMemo(() => {
    const snapshots: BreakdownHistorySnapshot[] = [];
    for (const result of results) {
      if (result.data) {
        snapshots.push(result.data);
      }
    }

    return { snapshots, isLoading: queryEnabled && results.some((r) => r.isLoading) };
  }, [results, queryEnabled]);
};

export const useBookEntity = (projectId: string | null, bookId: string | null) => useMediaEntity(projectId, bookId, "book");

/**
 * Order library rows by entity `info.created_date` (newest first — top-left in the 2-column grid).
 * Reuses the same React Query keys as {@link useMediaEntity} / card thumbnails.
 */
export const useLibraryItemsSortedByCreatedDate = <T extends LibraryItemSortKeySource & { readonly id: string }>(
  projectId: string | null,
  items: readonly T[]
): { readonly sortedItems: readonly T[]; readonly isSortPending: boolean } => {
  const enabled = typeof projectId === "string" && projectId.length > 0;

  const uniqueRefs = useMemo(() => {
    const seen = new Set<string>();
    const refs: Array<ReturnType<typeof librarySortEntityRef>> = [];

    for (const item of items) {
      const ref = librarySortEntityRef(item);
      const key = librarySortEntityCacheKey(ref);

      if (seen.has(key)) {
        continue;
      }

      seen.add(key);
      refs.push(ref);
    }

    return refs;
  }, [items]);

  const batchRefs = useMemo<MediaBatchItemRef[]>(() => uniqueRefs.map((ref) => ({ kind: ref.kind, id: ref.assetId })), [uniqueRefs]);
  const { data: byRef, isPending: detailsPending } = useMediaEntitiesBatch(projectId, batchRefs, enabled);

  const createdAtMsByKey = useMemo(() => {
    const map = new Map<string, number>();

    for (const ref of uniqueRefs) {
      const data = byRef?.get(mediaBatchRefKey({ kind: ref.kind, id: ref.assetId }));
      const created = parseCreatedDate(data?.info?.created_date);
      map.set(librarySortEntityCacheKey(ref), created?.getTime() ?? Number.NEGATIVE_INFINITY);
    }

    return map;
  }, [uniqueRefs, byRef]);

  const isSortPending = enabled && uniqueRefs.length > 0 && detailsPending;

  const sortedItems = useMemo(() => {
    if (items.length <= 1 || isSortPending) {
      return [...items];
    }

    return [...items].toSorted((a, b) => {
      const aTime = createdAtMsByKey.get(librarySortEntityCacheKey(librarySortEntityRef(a))) ?? Number.NEGATIVE_INFINITY;
      const bTime = createdAtMsByKey.get(librarySortEntityCacheKey(librarySortEntityRef(b))) ?? Number.NEGATIVE_INFINITY;

      if (bTime !== aTime) {
        return bTime - aTime;
      }

      return a.id.localeCompare(b.id);
    });
  }, [items, isSortPending, createdAtMsByKey]);

  return { sortedItems, isSortPending };
};
